import { IApplicationState, IBonus, OVERLAY_ALL_CLOSE } from '@spinwise/spinwise-core';
import styles from './PromoBonus.module.scss';
import { BaseText, EBaseTextSize } from '@spinwise/react-component-library/BaseText';
import { useState } from 'react';
import { ButtonLibrary } from 'Libraries/buttons.library';
import { EBrandTextColor, TypographyLibrary } from 'Libraries/typography.library';
import PromoGridTable from './PromoGridTable/PromoGridTable';
import classNames from 'classnames';

import BrandIcon, {
  BonusIconMap,
  BrandIconsSizes,
  ShapeColors,
  StatusIcons,
} from '../../../shared/BrandIcon/BrandIcon';
import { t } from 'i18next';
import parse from 'html-react-parser';
import { useDispatch, useSelector } from 'react-redux';
import router from 'next/router';
import { IconLibrary } from 'Libraries/icons.library';
import { EBrandIconVariants } from 'Libraries/variants.library';
import GameThumbnailManager from 'Components/shared/GameThumbnailManager/GameThumbnailManager';
import { EComponentVisibility } from '@spinwise/react-component-library/Global';
import { ButtonTextOnly } from '@spinwise/react-component-library/ButtonTextOnly';
import {
  EBaseButtonSize,
  EBaseButtonTypeAttribute,
  EButtonBorderRadiusStrength,
  EButtonTextAlign,
} from '@spinwise/react-component-library/ButtonAbstract';
import { ESquareThumbnailBreakpoints } from '@spinwise/react-component-library/BasicSquareThumbnail';
import { BaseIcon } from '@spinwise/react-component-library/BaseIcon';

interface IAccountBonusProps {
  bonus: IBonus;
  onPrimaryAction?: () => void;
  isSelected?: boolean;
  title?: string;
  description?: string;
  is_table_shown?: boolean;
  is_button_shown?: boolean;
  btn_text?: string;
  btn_url?: string;
  authorized_btn_url?: string;
  authorized_btn_text?: string;
  override_bonus_image?: string;
  override_bonus_terms?: string;
  integration_key?: any;
}

const PromoBonus = ({
  bonus,
  onPrimaryAction,
  isSelected,
  title,
  description,
  is_table_shown,
  is_button_shown,
  btn_text,
  btn_url,
  authorized_btn_url,
  authorized_btn_text,
  override_bonus_image,
  override_bonus_terms,
  integration_key,
}: IAccountBonusProps) => {
  const [isOpen, setIsOpen] = useState(is_table_shown);
  const dispatch = useDispatch();
  const loggedIn = useSelector((state: IApplicationState) => state.authReducer.auth?.success);
  const image = bonus?.image;

  if (bonus?.status === 'future') return null;

  const onButtonClick = () => {
    if (loggedIn && authorized_btn_url) {
      dispatch({
        type: OVERLAY_ALL_CLOSE,
        data: true,
      });
      router.push(authorized_btn_url);
    } else if (!loggedIn && btn_url) {
      dispatch({
        type: OVERLAY_ALL_CLOSE,
        data: true,
      });
      router.push(btn_url);
    } else {
      onPrimaryAction();
    }
  };

  return (
    <>
      <div className={`${styles.promo_bonus_container} ${isOpen ? styles.promo_bonus_container__expanded : ''}`}>
        <div className={`d-fl jc-fs ai-c`}>
          <div
            className={classNames(
              is_button_shown || (!is_button_shown && is_table_shown)
                ? styles.promo_bonus_container__big
                : styles.promo_bonus_container__big_without_btn,
            )}
            style={{ boxShadow: !isOpen ? 'none' : '' }}
          >
            <div className={styles.promo_bonus_container__icon}>
              <BrandIcon
                iconSize={BrandIconsSizes.Small}
                iconPath={
                  override_bonus_image
                    ? BonusIconMap?.[override_bonus_image]
                    : BonusIconMap?.[image]
                    ? BonusIconMap?.[image]
                    : BonusIconMap?.['Mystery.png']
                }
                statusPath={StatusIcons.None}
                shapeColor={ShapeColors.Default}
              />
            </div>
            <div className={classNames(styles.promo_bonus_container__text, 'd-fl fl-d-c jc-c ai-c')}>
              <BaseText value={title ?? bonus?.title} size={EBaseTextSize.Regular} color={EBrandTextColor.Black} />
              {description ? (
                <div className={styles.promo_bonus_container__text__description}>{parse(description)}</div>
              ) : (
                <BaseText value={bonus?.body} size={EBaseTextSize.Small} color={TypographyLibrary.Black05} />
              )}
            </div>
            {is_button_shown && (
              <div className={classNames(styles.promo_bonus_container__buttons, EComponentVisibility.TableteDesktop)}>
                <div className={styles.promo_btn_container}>
                  <ButtonTextOnly
                    label={
                      loggedIn && authorized_btn_text
                        ? authorized_btn_text
                        : !loggedIn && btn_text
                        ? btn_text
                        : t('read.more.btn')
                    }
                    size={EBaseButtonSize.Medium}
                    textSize={EBaseTextSize.Small}
                    type={ButtonLibrary.Bonuses}
                    borderRadius={EButtonBorderRadiusStrength.Seven}
                    typeAttribute={EBaseButtonTypeAttribute.Submit}
                    fullWidth={true}
                    alignText={EButtonTextAlign.Center}
                    onClick={() => onButtonClick()}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {bonus?.data?.free_rounds?.game && (
          <div className={classNames(`${styles.game_container} d-fl ai-c`)} onClick={() => onButtonClick()}>
            <div>
              <GameThumbnailManager
                game={bonus?.data?.free_rounds?.game}
                integrationKeys={integration_key}
                isSquare={true}
                size={ESquareThumbnailBreakpoints.SM}
                isTextContainer={false}
              />
            </div>
            <div className={classNames(`${styles.card_advanced} d-fl jc-sb ai-c`)}>
              <div>
                <p className={styles.card_advanced__name}>{bonus?.data?.free_rounds?.game?.name}</p>
                <p className={styles.card_advanced__provider}>{bonus?.data?.free_rounds?.game?.provider?.name}</p>
              </div>
              <div onClick={() => onButtonClick()}>
                <BaseIcon
                  isActive={false}
                  isMasked={true}
                  isStatic={true}
                  iconName={IconLibrary.ArrowRight}
                  variant={EBrandIconVariants.Default}
                />
              </div>
            </div>
          </div>
        )}

        <div className={`d-fl jc-fs ai-c`}>
          {bonus && isOpen && <PromoGridTable bonus={bonus} bonus_terms={override_bonus_terms}></PromoGridTable>}
        </div>

        {is_button_shown && (
          <div className={classNames('d-fl jc-fs ai-c', EComponentVisibility.MobileOnly)}>
            <div className={styles.promo_bonus_container__buttons}>
              <div className={styles.promo_btn_container}>
                <ButtonTextOnly
                  label={
                    loggedIn && authorized_btn_text
                      ? authorized_btn_text
                      : !loggedIn && btn_text
                      ? btn_text
                      : t('read.more.btn')
                  }
                  size={EBaseButtonSize.Medium}
                  type={ButtonLibrary.Bonuses}
                  borderRadius={EButtonBorderRadiusStrength.Seven}
                  typeAttribute={EBaseButtonTypeAttribute.Submit}
                  fullWidth={true}
                  alignText={EButtonTextAlign.Center}
                  onClick={() => onButtonClick()}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PromoBonus;
